import { supabase } from '../supabaseClient';

// Versão atual do app (será atualizada automaticamente do package.json no build)
const CURRENT_VERSION = process.env.REACT_APP_VERSION || require('../../package.json').version;

export class VersionService {
  /**
   * Verifica se há uma nova versão disponível
   * @returns {Promise<{hasUpdate: boolean, currentVersion: string, latestVersion: string, message: string}>}
   */
  static async checkForUpdates() {
    try {
      // Busca a versão mais recente do servidor (pode ser um arquivo JSON ou tabela no Supabase)
      const { data, error } = await supabase
        .from('app_config')
        .select('latest_version, update_message, force_update')
        .eq('config_key', 'version')
        .single();

      if (error || !data) {
        console.log('Não foi possível verificar atualizações');
        return {
          hasUpdate: false,
          currentVersion: CURRENT_VERSION,
          latestVersion: CURRENT_VERSION,
          message: '',
          forceUpdate: false
        };
      }

      const latestVersion = data.latest_version;
      const hasUpdate = this.compareVersions(CURRENT_VERSION, latestVersion) < 0;

      return {
        hasUpdate,
        currentVersion: CURRENT_VERSION,
        latestVersion,
        message: data.update_message || 'Uma nova versão está disponível!',
        forceUpdate: data.force_update || false
      };
    } catch (e) {
      console.error('Erro ao verificar atualizações:', e);
      return {
        hasUpdate: false,
        currentVersion: CURRENT_VERSION,
        latestVersion: CURRENT_VERSION,
        message: '',
        forceUpdate: false
      };
    }
  }

  /**
   * Compara duas versões no formato X.Y.Z
   * @returns {number} -1 se v1 < v2, 0 se iguais, 1 se v1 > v2
   */
  static compareVersions(v1, v2) {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 < num2) return -1;
      if (num1 > num2) return 1;
    }

    return 0;
  }

  /**
   * Registra que o usuário foi notificado sobre a versão
   */
  static markVersionNotified(version) {
    localStorage.setItem('lastNotifiedVersion', version);
  }

  /**
   * Verifica se já notificou o usuário sobre esta versão
   */
  static wasVersionNotified(version) {
    return localStorage.getItem('lastNotifiedVersion') === version;
  }

  /**
   * Força atualização da página (limpa cache)
   */
  static forceUpdate() {
    // Limpa cache do service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    
    // Limpa cache do navegador
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Recarrega a página
    window.location.reload(true);
  }

  /**
   * Abre a loja de aplicativos (Android/iOS) ou faz download direto do APK
   */
  static async openStore() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      // Android - verifica se deve abrir Play Store ou baixar APK
      const { data } = await supabase
        .from('app_config')
        .select('download_url, use_play_store')
        .eq('config_key', 'version')
        .single();

      // Se tiver URL de download direto e não usar Play Store
      if (data?.download_url && !data?.use_play_store) {
        // Download direto do APK
        this.downloadAPK(data.download_url);
      } else {
        // Abre Play Store (substitua com o ID do seu app)
        window.open('https://play.google.com/store/apps/details?id=com.forecastagro.app', '_blank');
      }
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      // Link da App Store (substitua com o ID do seu app)
      window.open('https://apps.apple.com/app/idXXXXXXXXX', '_blank');
    } else {
      // Para web, força atualização
      this.forceUpdate();
    }
  }

  /**
   * Faz download direto do APK e solicita instalação
   */
  static downloadAPK(downloadUrl) {
    // Capacitor FileOpener para Android
    if (window.Capacitor && window.Capacitor.Plugins) {
      this.downloadAPKNative(downloadUrl);
    } else {
      // Fallback: abre URL em nova aba
      window.open(downloadUrl, '_blank');
    }
  }

  /**
   * Download nativo usando Capacitor
   */
  static async downloadAPKNative(downloadUrl) {
    try {
      const { Capacitor } = window;
      
      if (!Capacitor || !Capacitor.isNativePlatform()) {
        // Se não for nativo, abre URL em nova aba
        window.open(downloadUrl, '_blank');
        return;
      }

      // Importa plugins necessários
      const { Filesystem } = await import('@capacitor/filesystem');
      const { FileOpener } = await import('@capacitor-community/file-opener');

      // Mostra loading
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); 
                    background: white; padding: 24px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                    z-index: 999999; text-align: center;">
          <div style="font-size: 48px; margin-bottom: 12px;">📥</div>
          <div style="font-weight: 700; font-size: 18px; color: #333; margin-bottom: 8px;">
            Baixando atualização...
          </div>
          <div id="download-progress" style="color: #666; font-size: 14px;">Preparando download...</div>
        </div>
      `;
      document.body.appendChild(notification);

      // Faz download usando fetch
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Converte blob para base64
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Remove o prefixo "data:application/...;base64," do base64
      const base64 = base64Data.split(',')[1];

      // Salva arquivo no cache
      const fileName = 'forecast-agro-update.apk';
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: 'CACHE'
      });

      // Remove loading
      document.body.removeChild(notification);

      // Abre APK para instalação
      await FileOpener.open({
        filePath: savedFile.uri,
        contentType: 'application/vnd.android.package-archive',
        openWithDefault: true
      });

      console.log('APK pronto para instalação');
    } catch (e) {
      console.error('Erro ao baixar APK:', e);
      
      // Remove loading se existir
      try {
        const notification = document.querySelector('[id*="download"]')?.parentElement?.parentElement;
        if (notification) document.body.removeChild(notification);
      } catch {}
      
      alert('Erro ao baixar atualização: ' + e.message + '\n\nAbrindo navegador para download manual...');
      
      // Fallback: abre URL no navegador
      window.open(downloadUrl, '_blank');
    }
  }
}

export default VersionService;
